import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '@styles/theme';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  isLoading?: boolean;
}

const LoadingDot = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  font-family: ${theme.typography.fontFamily.primary};
  font-weight: ${theme.typography.fontWeight.medium};
  border-radius: ${theme.borderRadius.full};
  transition: all ${theme.transitions.fast};
  cursor: pointer;
  outline: none;
  border: 1px solid transparent;
  box-shadow: ${theme.shadows.sm};
  transform: translateY(0);

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }

  ${(props) => {
    switch (props.size) {
      case 'sm':
        return css`
          padding: ${theme.spacing.sm} ${theme.spacing.md};
          font-size: ${theme.typography.fontSize.sm};
          min-height: 44px;
        `;
      case 'lg':
        return css`
          padding: ${theme.spacing.md} ${theme.spacing.xl};
          font-size: ${theme.typography.fontSize.lg};
          min-height: 52px;
        `;
      default:
        return css`
          padding: ${theme.spacing.sm} ${theme.spacing.lg};
          font-size: ${theme.typography.fontSize.base};
          min-height: 46px;
        `;
    }
  }}

  ${(props) => {
    switch (props.variant) {
      case 'primary':
        return css`
          background-color: ${theme.colors.primary};
          color: ${theme.colors.text.inverse};
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primaryDark};
            box-shadow: ${theme.shadows.md};
          }
          &:active:not(:disabled) {
            transform: translateY(1px) scale(0.99);
          }
        `;
      case 'secondary':
        return css`
          background-color: ${theme.colors.secondary};
          color: ${theme.colors.text.inverse};
          &:hover:not(:disabled) {
            opacity: 0.9;
            box-shadow: ${theme.shadows.md};
          }
        `;
      case 'outline':
        return css`
          background-color: ${theme.colors.background.default};
          color: ${theme.colors.primary};
          border-color: ${theme.colors.primary};
          box-shadow: none;
          &:hover:not(:disabled) {
            background-color: ${theme.colors.background.secondary};
          }
        `;
      case 'ghost':
        return css`
          background-color: transparent;
          color: ${theme.colors.text.primary};
          box-shadow: none;
          &:hover:not(:disabled) {
            background-color: ${theme.colors.background.secondary};
          }
        `;
      case 'danger':
        return css`
          background-color: ${theme.colors.error};
          color: ${theme.colors.text.inverse};
          &:hover:not(:disabled) {
            opacity: 0.95;
            box-shadow: ${theme.shadows.md};
          }
        `;
      default:
        return css`
          background-color: ${theme.colors.primary};
          color: ${theme.colors.text.inverse};
        `;
    }
  }}

  ${(props) =>
    props.fullWidth &&
    css`
      width: 100%;
    `}
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  isLoading,
  disabled,
  ...props
}) => {
  return (
    <StyledButton disabled={disabled || isLoading} {...props}>
      {isLoading ? (
        <>
          <LoadingDot aria-hidden />
          加载中...
        </>
      ) : children}
    </StyledButton>
  );
};

